/**
 * Default creators and upgraders for {@link PlotPreferences}.
 * 
 * @packageDocumentation
 * @module PlotPreferences
 * @public
 */

import {
  AxisKey,
  AxisPreferences,
  CurveId,
  CurvePreferences,
  CurveRelation,
  DEFAULT_TIMESERIES_AXIS_LABEL_MODE,
  PlotPreferences,
  PlotPreferencesV2,
  RefreshIntervalOption,
  SourceSlotIndex,
  ThresholdPrefs,
  TimeRangeOption,
  TimeseriesAxisLabelMode,
  ToolBarPreferences,
  ToolBarPreferencesV2,
} from './plotTypes';
import {
  assignDefaultTimezoneForNewCurve,
  buildCurveToTimezoneFromInitSettings,
  removeCurveTimezone,
  upgradeCurveToTimezone,
} from './curveTimezoneMapping';

const DEFAULT_PLOTLY_COLORS = [
  '#1f77b4', // muted blue
  '#ff7f0e', // safety orange
  '#2ca02c', // cooked asparagus green
  '#d62728', // brick red
  '#9467bd', // muted purple
  '#8c564b', // chestnut brown
  '#e377c2', // raspberry yogurt pink
  '#7f7f7f', // middle gray
  '#bcbd22', // curry yellow-green
  '#17becf', // blue-teal
];

export const DEFAULT_REFRESH_INTERVAL_OPTIONS: RefreshIntervalOption[] = [
  { quantity: 1, unit: 'minute' },
  { quantity: 2, unit: 'minute' },
  { quantity: 5, unit: 'minute' },
  { quantity: 10, unit: 'minute' },
];

export const DEFAULT_TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { quantity: 1, unit: 'minute' },
  { quantity: 1, unit: 'hour' },
  { quantity: 1, unit: 'day' },
  { quantity: 1, unit: 'week' },
  { quantity: 1, unit: 'month' },
  { quantity: 1, unit: 'year' },
];

const normalizePositiveQuantity = (value: number): number =>
  Number.isFinite(value) && value > 0 ? Math.round(value) : 1;

const refreshIntervalOptionKey = (option: RefreshIntervalOption): string =>
  `${normalizePositiveQuantity(option.quantity)}:${option.unit}`;

const timeRangeOptionKey = (option: TimeRangeOption): string =>
  `${normalizePositiveQuantity(option.quantity)}:${option.unit}`;

const uniqueRefreshIntervalOptions = (
  values: RefreshIntervalOption[]
): RefreshIntervalOption[] => {
  const seen = new Set<string>();

  return values.reduce<RefreshIntervalOption[]>((acc, option) => {
    const normalizedOption: RefreshIntervalOption = {
      quantity: normalizePositiveQuantity(option.quantity),
      unit: option.unit,
    };
    const key = refreshIntervalOptionKey(normalizedOption);

    if (!seen.has(key)) {
      seen.add(key);
      acc.push(normalizedOption);
    }

    return acc;
  }, []);
};

const uniqueTimeRangeOptions = (
  values: TimeRangeOption[]
): TimeRangeOption[] => {
  const seen = new Set<string>();

  return values.reduce<TimeRangeOption[]>((acc, option) => {
    const normalizedOption: TimeRangeOption = {
      quantity: normalizePositiveQuantity(option.quantity),
      unit: option.unit,
    };
    const key = timeRangeOptionKey(normalizedOption);

    if (!seen.has(key)) {
      seen.add(key);
      acc.push(normalizedOption);
    }

    return acc;
  }, []);
};

export function createDefaultToolBarPreferences(
  prefs: Partial<ToolBarPreferencesV2> = {}
): ToolBarPreferencesV2 {
  const refreshIntervalOptions = uniqueRefreshIntervalOptions(
    prefs.refreshIntervalOptions ?? DEFAULT_REFRESH_INTERVAL_OPTIONS
  );
  const timeRangeOptions = uniqueTimeRangeOptions(
    prefs.timeRangeOptions ?? DEFAULT_TIME_RANGE_OPTIONS
  );
  const selectedRefreshInterval = prefs.selectedRefreshInterval
    ? {
        quantity: normalizePositiveQuantity(
          prefs.selectedRefreshInterval.quantity
        ),
        unit: prefs.selectedRefreshInterval.unit,
      }
    : undefined;

  return {
    hide: prefs.hide ?? true,
    useTimeRangePills: prefs.useTimeRangePills ?? true,
    useReloadButton: prefs.useReloadButton ?? false,
    useRefreshIntervalSelector: prefs.useRefreshIntervalSelector ?? false,
    selectedRefreshInterval:
      selectedRefreshInterval &&
      refreshIntervalOptions.some(
        (option) =>
          refreshIntervalOptionKey(option) ===
          refreshIntervalOptionKey(selectedRefreshInterval)
      )
        ? selectedRefreshInterval
        : undefined,
    refreshIntervalOptions,
    timeRangeOptions,
  };
}

export function upgradeToolBarPreferences(
  prefs?: ToolBarPreferences
): ToolBarPreferencesV2 {
  if (!prefs) {
    return createDefaultToolBarPreferences();
  }

  return createDefaultToolBarPreferences({
    ...prefs,
    selectedRefreshInterval:
      'selectedRefreshInterval' in prefs
        ? prefs.selectedRefreshInterval
        : undefined,
    refreshIntervalOptions:
      'refreshIntervalOptions' in prefs
        ? prefs.refreshIntervalOptions
        : DEFAULT_REFRESH_INTERVAL_OPTIONS,
    timeRangeOptions:
      'timeRangeOptions' in prefs
        ? prefs.timeRangeOptions
        : DEFAULT_TIME_RANGE_OPTIONS,
  });
}

/**
 * Upgrade plot preferences to the new format.
 */
export function upgradePlotPreferences(
  prefs?: PlotPreferences
): PlotPreferencesV2 {

  // -- create initial plot preferences --
  if (!prefs) {
    return createInitialPlotPreferences();
  }
  // -- upgrade axis preferences --
  const upgradedAxisPrefs = Object.fromEntries(
    Object.entries(prefs.axisPrefs).map(([axisKey, axisPrefs]) => [
      axisKey,
      upgradeAxisPreferences(axisPrefs),
    ])
  ) as Record<AxisKey, AxisPreferences>;

  // -- return with fallback to initial preferences --
  return {
    ...prefs,
    version: 2,
    curveOrder: prefs.curveOrder ?? Object.keys(prefs.curvePrefs),
    curveToSourceIndex: prefs.curveToSourceIndex ?? {},
    curveToTimezone: upgradeCurveToTimezone(prefs.curveToTimezone),
    timeseriesAxisLabelMode:
      prefs.timeseriesAxisLabelMode ?? DEFAULT_TIMESERIES_AXIS_LABEL_MODE,
    curveRelations: prefs.curveRelations ?? [],
    thresholdPrefs: prefs.thresholdPrefs,
    axisPrefs: upgradedAxisPrefs,
    toolBarPrefs: upgradeToolBarPreferences(prefs.toolBarPrefs),
  };
}

/**
 * Upgrade deprecated axis preferences (in {@link AxisPreferences}) to the new format.
 */
export function upgradeAxisPreferences(
  axis: AxisPreferences
): AxisPreferences {
  const hasLegacyLocked = axis.locked !== undefined;
  const hasUseBounds = axis.useBounds !== undefined;
  const hasZoomEnabled = axis.zoomEnabled !== undefined;

  if (!hasLegacyLocked && hasUseBounds && hasZoomEnabled) {
    return axis;
  }

  const locked = axis.locked ?? false;
  const useBounds =
    axis.useBounds ??
    (locked === true && axis.min !== undefined && axis.max !== undefined);
  const zoomEnabled = axis.zoomEnabled ?? locked !== true;

  const { locked: _locked, ...rest } = axis;

  return {
    ...rest,
    useBounds,
    zoomEnabled,
  };
}

export function getColorByIndex(index: number) {
  return DEFAULT_PLOTLY_COLORS[index % DEFAULT_PLOTLY_COLORS.length];
}

export function createInitialCurvePreferences(
  prefs: Partial<CurvePreferences> = {}
): CurvePreferences {
  return {
    displayName: prefs.displayName ?? '',
    color: prefs.color ?? undefined,
    lineStyle: prefs.lineStyle ?? 'solid',
    thicknessPx: prefs.thicknessPx ?? 2,
    visible: prefs.visible ?? true,
    shape: prefs.shape ?? 'linear',
  };
}

/**
 * Create initial axis preferences from a partial {@link AxisPreferences} object.
 */
export function createInitialAxisPreferences(
  prefs: Partial<AxisPreferences> = {}
): AxisPreferences {
  if (
    prefs.locked !== undefined ||
    prefs.useBounds === undefined ||
    prefs.zoomEnabled === undefined
  ) {
    return upgradeAxisPreferences({
      displayName: prefs.displayName ?? '',
      min: prefs.min,
      max: prefs.max,
      locked: prefs.locked,
      useBounds: prefs.useBounds,
      zoomEnabled: prefs.zoomEnabled,
      visible: prefs.visible ?? true,
      isXAxis: prefs.isXAxis ?? false,
    });
  }

  return {
    displayName: prefs.displayName ?? '',
    min: prefs.min,
    max: prefs.max,
    useBounds: prefs.useBounds,
    zoomEnabled: prefs.zoomEnabled,
    visible: prefs.visible ?? true,
    isXAxis: prefs.isXAxis ?? false,
  };
}

export function getNextYAxisKey(
  axisPrefs: Record<AxisKey, AxisPreferences>
): AxisKey {
  const nextIndex =
    Math.max(
      0,
      ...Object.keys(axisPrefs)
        .filter((axisKey) => axisKey.startsWith('y'))
        .map((axisKey) => Number(axisKey.slice(1)) || 0)
    ) + 1;

  return `y${nextIndex}` as AxisKey;
}

export function createNextYAxis(axisPrefs: Record<AxisKey, AxisPreferences>): {
  axisKey: AxisKey;
  axisPreferences: AxisPreferences;
} {
  const axisKey = getNextYAxisKey(axisPrefs);
  const axisIndex = Number(axisKey.slice(1));

  return {
    axisKey,
    axisPreferences: createInitialAxisPreferences({
      displayName: `Y-Axis ${axisIndex}`,
      isXAxis: false,
      useBounds: false,
      zoomEnabled: true,
    }),
  };
}

// -- Create initial PlotPreferences

type InitPair = [CurveId, AxisKey] | [CurveId, AxisKey, string, string];

interface CurveInitSettings {
  curveId: CurveId;
  axisKey: AxisKey;
  sourceIndex?: SourceSlotIndex;
  /** IANA timezone for the source backing this curve; defaults to UTC when omitted. */
  timezone?: string;
  curveName?: string;
  axisName?: string;
  color?: string; // rgb/rgba/hex/css
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  thicknessPx?: number;
  shape?: 'spline' | 'linear' | 'vhv' | 'hvh' | 'vh' | 'hv'; // additional line settings
  isX?: boolean;
}

interface AdditionalSettings {
  showRangeSlider?: boolean;
  curveRelations?: CurveRelation[];
  thresholdPrefs?: ThresholdPrefs;
  toolBarPrefs?: Partial<ToolBarPreferencesV2>;
  timeseriesAxisLabelMode?: TimeseriesAxisLabelMode;
}

export function createInitialPlotPreferences(
  dataSourceSettings: CurveInitSettings[] = [],
  order?: (k: AxisKey) => number,
  additionalSettings: AdditionalSettings = {
    showRangeSlider: false,
    curveRelations: [],
  }
): PlotPreferencesV2 {
  const resolvedAdditionalSettings = {
    showRangeSlider: additionalSettings.showRangeSlider ?? false,
    curveRelations: additionalSettings.curveRelations ?? [],
    thresholdPrefs: additionalSettings.thresholdPrefs,
    toolBarPrefs: createDefaultToolBarPreferences(
      additionalSettings.toolBarPrefs ?? {}
    ),
  };
  const curvePrefs: Record<CurveId, CurvePreferences> = {};
  const curveOrder: CurveId[] = [];
  const curveToSourceIndex: Record<CurveId, SourceSlotIndex> = {};
  const curveToTimezone = buildCurveToTimezoneFromInitSettings(
    dataSourceSettings.map(({ curveId, timezone }) => ({ curveId, timezone }))
  );
  const axisPrefs: Record<AxisKey, AxisPreferences> = {};
  const dataSourceToAxis: Record<CurveId, AxisKey> = {};
  const yAxisOrder: AxisKey[] = [];
  const seenAxes = new Set<AxisKey>();

  let colorIndex = 0;

  for (const dss of dataSourceSettings) {
    const ds = dss.curveId;
    const axis = dss.axisKey;
    const sourceIndex = dss.sourceIndex;
    const curveName = dss.curveName;
    const axisName = dss.axisName;
    const isX = dss.isX;

    if (!dataSourceToAxis[ds]) {
      dataSourceToAxis[ds] = axis;
    }

    if (!curvePrefs[ds]) {
      curvePrefs[ds] = createInitialCurvePreferences({
        displayName: curveName ? curveName : undefined,
        color: dss.color ?? (isX ? 'black' : getColorByIndex(colorIndex)),
        lineStyle: dss.lineStyle,
        thicknessPx: dss.thicknessPx,
        shape: dss.shape ?? 'linear',
      });
      curveOrder.push(ds);
      if (sourceIndex !== undefined) {
        curveToSourceIndex[ds] = sourceIndex;
      }
    }

    if (!isX) colorIndex++;

    if (!axisPrefs[axis]) {
      axisPrefs[axis] = createInitialAxisPreferences({
        ...(axisName ? { displayName: axisName } : {}),
        isXAxis: isX,
      });
    }

    if (!seenAxes.has(axis)) {
      yAxisOrder.push(axis);
      seenAxes.add(axis);
    }
  }

  if (order) yAxisOrder.sort((a, b) => order(a) - order(b));

  return {
    version: 2,
    curvePrefs,
    curveOrder,
    curveToSourceIndex,
    curveToTimezone,
    timeseriesAxisLabelMode:
      additionalSettings.timeseriesAxisLabelMode ??
      DEFAULT_TIMESERIES_AXIS_LABEL_MODE,
    axisPrefs,
    dataSourceToAxis,
    yAxisOrder,
    ...resolvedAdditionalSettings,
  };
}

// -- Add data source(s)

export function addDataSource(
  prefs: PlotPreferences,
  ds: CurveId,
  axis: AxisKey,
  curveName?: string,
  axisName?: string,
  order?: (k: AxisKey) => number
): PlotPreferencesV2 {
  const resolvedPrefs = upgradePlotPreferences(prefs);
  const currentCurveOrder =
    resolvedPrefs.curveOrder ?? Object.keys(resolvedPrefs.curvePrefs);
  const currentCurveToSourceIndex = resolvedPrefs.curveToSourceIndex ?? {};
  const newPrefs: PlotPreferencesV2 = {
    ...resolvedPrefs,
    curvePrefs: { ...resolvedPrefs.curvePrefs },
    curveOrder: [...currentCurveOrder],
    curveToSourceIndex: { ...currentCurveToSourceIndex },
    curveToTimezone: { ...(resolvedPrefs.curveToTimezone ?? {}) },
    axisPrefs: { ...resolvedPrefs.axisPrefs },
    dataSourceToAxis: { ...resolvedPrefs.dataSourceToAxis },
    yAxisOrder: [...resolvedPrefs.yAxisOrder],
    curveRelations: [...(resolvedPrefs.curveRelations ?? [])],
    toolBarPrefs: { ...resolvedPrefs.toolBarPrefs },
  };

  newPrefs.dataSourceToAxis[ds] = axis;

  if (!newPrefs.curvePrefs[ds]) {
    newPrefs.curvePrefs[ds] = createInitialCurvePreferences(
      curveName ? { displayName: curveName } : {}
    );
    newPrefs.curveOrder?.push(ds);
    newPrefs.curveToTimezone = assignDefaultTimezoneForNewCurve(
      newPrefs.curveToTimezone ?? {},
      ds
    );
    if (newPrefs.curveToSourceIndex?.[ds] === undefined) {
      // safe: we ensured above that curveToSourceIndex is defined, even if it might just be an empty object
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      newPrefs.curveToSourceIndex![ds] =
        Object.keys(newPrefs.curvePrefs).length - 1;
    }
  }

  if (!newPrefs.axisPrefs[axis]) {
    newPrefs.axisPrefs[axis] = createInitialAxisPreferences(
      axisName ? { displayName: axisName } : {}
    );
  }

  if (!newPrefs.yAxisOrder.includes(axis)) {
    newPrefs.yAxisOrder.push(axis);
  }

  if (order) newPrefs.yAxisOrder.sort((a, b) => order(a) - order(b));

  return newPrefs;
}

export function addDataSources(
  prefs: PlotPreferences,
  pairs: InitPair[],
  order?: (k: AxisKey) => number
): PlotPreferencesV2 {
  let updated = upgradePlotPreferences(prefs);
  for (const p of pairs) {
    const [ds, axis, curveName, axisName] = p;
    updated = addDataSource(updated, ds, axis, curveName, axisName, order);
  }
  return updated;
}

// -- Remove data source(s)

export function removeDataSource(
  prefs: PlotPreferences,
  key: CurveId,
  order?: (k: AxisKey) => number
): PlotPreferencesV2 {
  const resolvedPrefs = upgradePlotPreferences(prefs);
  const currentCurveOrder =
    resolvedPrefs.curveOrder ?? Object.keys(resolvedPrefs.curvePrefs);
  const currentCurveToSourceIndex = resolvedPrefs.curveToSourceIndex ?? {};
  const newPrefs: PlotPreferencesV2 = {
    ...resolvedPrefs,
    curvePrefs: { ...resolvedPrefs.curvePrefs },
    curveOrder: [...currentCurveOrder],
    curveToSourceIndex: { ...currentCurveToSourceIndex },
    axisPrefs: { ...resolvedPrefs.axisPrefs },
    dataSourceToAxis: { ...resolvedPrefs.dataSourceToAxis },
    yAxisOrder: [...resolvedPrefs.yAxisOrder],
    curveRelations: [...(resolvedPrefs.curveRelations ?? [])],
    toolBarPrefs: { ...resolvedPrefs.toolBarPrefs },
  };

  const axis = newPrefs.dataSourceToAxis[key];

  delete newPrefs.dataSourceToAxis[key];
  delete newPrefs.curvePrefs[key];
  newPrefs.curveOrder = (newPrefs.curveOrder ?? []).filter((k) => k !== key);
  if (newPrefs.curveToSourceIndex) {
    delete newPrefs.curveToSourceIndex[key];
  }
  newPrefs.curveToTimezone = removeCurveTimezone(
    newPrefs.curveToTimezone ?? {},
    key
  );

  // if the data source's axis is not linked to any other data source, remove it
  if (axis && !Object.values(newPrefs.dataSourceToAxis).includes(axis)) {
    delete newPrefs.axisPrefs[axis];
    newPrefs.yAxisOrder = newPrefs.yAxisOrder.filter((a) => a !== axis);
  }

  if (order) newPrefs.yAxisOrder.sort((a, b) => order(a) - order(b));

  return newPrefs;
}

export function removeDataSources(
  prefs: PlotPreferences,
  keys: CurveId[],
  order?: (k: AxisKey) => number
): PlotPreferencesV2 {
  let updated = upgradePlotPreferences(prefs);
  for (const k of keys) {
    updated = removeDataSource(updated, k, order);
  }
  return updated;
}

// -- Axis preferences

export function setAxisPreferences(
  prefs: PlotPreferences,
  axisKey: AxisKey,
  axisPrefs: AxisPreferences
): PlotPreferencesV2 {
  const resolvedPrefs = upgradePlotPreferences(prefs);

  return {
    ...resolvedPrefs,
    axisPrefs: {
      ...resolvedPrefs.axisPrefs,
      [axisKey]: { ...axisPrefs },
    },
  };
}

export function getAxisPreferences(
  prefs: PlotPreferences,
  axisKey: AxisKey
): AxisPreferences | undefined {
  const a = prefs.axisPrefs[axisKey];
  return a ? { ...a } : undefined;
}

export function updateAxisPreferences(
  prefs: PlotPreferences,
  axisKey: AxisKey,
  partial: Partial<AxisPreferences>
): PlotPreferencesV2 {
  const existing = prefs.axisPrefs[axisKey] ?? createInitialAxisPreferences();
  return setAxisPreferences(prefs, axisKey, { ...existing, ...partial });
}

// -- Curve preferences

export function setCurvePreferences(
  prefs: PlotPreferences,
  dsKey: CurveId,
  curve: CurvePreferences
): PlotPreferencesV2 {
  const resolvedPrefs = upgradePlotPreferences(prefs);

  return {
    ...resolvedPrefs,
    curvePrefs: {
      ...resolvedPrefs.curvePrefs,
      [dsKey]: { ...curve },
    },
  };
}

export function getCurvePreferences(
  prefs: PlotPreferences,
  dsKey: CurveId
): CurvePreferences | undefined {
  const c = prefs.curvePrefs[dsKey];
  return c ? { ...c } : undefined;
}

export function updateCurvePreferences(
  prefs: PlotPreferences,
  dsKey: CurveId,
  partial: Partial<CurvePreferences>
): PlotPreferencesV2 {
  const existing = prefs.curvePrefs[dsKey] ?? createInitialCurvePreferences();
  return setCurvePreferences(prefs, dsKey, { ...existing, ...partial });
}

// -- Axis mapping

export function setAxis(
  prefs: PlotPreferences,
  dsKey: CurveId,
  axisKey: AxisKey
): PlotPreferencesV2 {
  const resolvedPrefs = upgradePlotPreferences(prefs);

  return {
    ...resolvedPrefs,
    dataSourceToAxis: {
      ...resolvedPrefs.dataSourceToAxis,
      [dsKey]: axisKey,
    },
  };
}

export function getAxis(
  prefs: PlotPreferences,
  dsKey: CurveId
): AxisKey | undefined {
  return prefs.dataSourceToAxis[dsKey];
}

export function updateAxis(
  prefs: PlotPreferences,
  dsKey: CurveId,
  axisKey: AxisKey
): PlotPreferencesV2 {
  return setAxis(prefs, dsKey, axisKey);
}

// -- ToolBar preferences

export function setToolBarPreferences(
  prefs: PlotPreferences,
  toolBarPrefs: ToolBarPreferences
): PlotPreferencesV2 {
  const resolvedPrefs = upgradePlotPreferences(prefs);

  return {
    ...resolvedPrefs,
    toolBarPrefs: upgradeToolBarPreferences(toolBarPrefs),
  };
}

export function getToolBarPreferences(
  prefs: PlotPreferences
): ToolBarPreferencesV2 {
  return { ...upgradePlotPreferences(prefs).toolBarPrefs };
}

export function updateToolBarPreferences(
  prefs: PlotPreferences,
  partial: Partial<ToolBarPreferencesV2>
): PlotPreferencesV2 {
  const currentToolBarPrefs = getToolBarPreferences(prefs);
  const nextToolBarPrefs = createDefaultToolBarPreferences({
    ...currentToolBarPrefs,
    ...partial,
  });

  const selectedRefreshInterval = nextToolBarPrefs.selectedRefreshInterval;

  if (
    selectedRefreshInterval !== undefined &&
    !nextToolBarPrefs.refreshIntervalOptions.some(
      (option) =>
        refreshIntervalOptionKey(option) ===
        refreshIntervalOptionKey(selectedRefreshInterval)
    )
  ) {
    nextToolBarPrefs.selectedRefreshInterval = undefined;
  }

  return setToolBarPreferences(prefs, nextToolBarPrefs);
}
